<script lang="ts" setup>
import { computed } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppSvgIcon } from '@libs/ui';

import { TripType } from './TripTypeSelectorPanel.model';

interface AppTripTypeCardProps {
  tripType: TripType;
  selected: boolean;
}

const props = defineProps<AppTripTypeCardProps>();

interface AppTripTypeCardEmits {
  (eventName: 'edit'): void;
}

const emit = defineEmits<AppTripTypeCardEmits>();

const browserStore = useBrowserStore();

const tripTypeIcon = computed(() => {
  if (props.tripType === TripType.Car) {
    return 'car';
  } else if (props.tripType === TripType.Hotel) {
    return 'bedOutlined';
  } else if (props.tripType === TripType.Ferry) {
    return 'ferry';
  }
  return 'airplane';
});

const iconSize = computed(() =>
  browserStore.isSmallerThanMediumMin ? '18' : '24'
);
</script>

<template>
  <div
    class="trip-type-card"
    :class="{ selected: props.selected, [props.tripType]: true }"
  >
    <div class="trip-type-card-header">
      <div class="trip-type-card-icon">
        <AppSvgIcon class="icon" :name="tripTypeIcon" :size="iconSize" />
      </div>
      <div class="trip-type-card-info">
        <span class="trip-type-title">
          <slot name="title" />
        </span>
        <span class="trip-type-description">
          <slot name="description" />
        </span>
      </div>
    </div>
    <div v-if="props.tripType !== 'hotel'" class="trip-type-card-details">
      <div class="trip-type-card-location">
        <span class="trip-type-card-location-label">
          <slot name="location-label" />
        </span>
        <div class="trip-type-card-location-value">
          <span class="trip-type-card-location-destination-value">
            <slot name="destination" />
          </span>
          <span class="trip-type-card-edit" @click="emit('edit')">
            <slot name="edit-text" />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.trip-type-card {
  //Temporary color variables. Once the experiment is done, we should talk to Almir to add them to the `variables.css` file.
  --hotel-color: #206f6f;
  --hotel-background-color: rgba(32, 111, 111, 0.1);
  --flight-color: #462170;
  --flight-background-color: rgba(70, 33, 112, 0.1);
  --ferry-color: #ff7569;
  --ferry-background-color: rgba(255, 117, 105, 0.1);
  --height-trip-type-card: 175px;
  --height-trip-type-card-small: 141px;

  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-secondary-85);
  border-radius: var(--spacing-12);
  padding: var(--spacing-24);
  background-color: var(--color-white);
  cursor: pointer;
  margin: var(--spacing-2);
  transition: background-color var(--transition-time-medium) ease;
  @include on-hover {
    background-color: var(--color-secondary-97);
  }

  &:first-child {
    margin-top: var(--spacing-2);
  }

  &.hotel {
    padding-top: 0;
    padding-bottom: 0;
    .trip-type-card-header {
      border-bottom: none;
      padding-bottom: 0;
      margin-bottom: 0;
      height: var(--height-trip-type-card);
      @include for-small-devices-max {
        height: var(--height-trip-type-card-small);
      }
      .trip-type-card-icon {
        border-color: var(--hotel-color);

        .icon {
          color: var(--hotel-color);
        }
      }
    }
  }
  &.flight {
    .trip-type-card-header {
      .trip-type-card-icon {
        border-color: var(--flight-color);
        .icon {
          color: var(--flight-color);
        }
      }
    }
  }
  &.ferry {
    .trip-type-card-header {
      .trip-type-card-icon {
        border-color: var(--ferry-color);
        .icon {
          color: var(--ferry-color);
        }
      }
    }
  }
  &.selected {
    box-shadow: 0 0 0 var(--spacing-2) var(--color-secondary-40);
    &.hotel {
      background-color: var(--hotel-background-color);
      box-shadow: 0 0 0 var(--spacing-2) var(--hotel-color);
    }
    &.flight {
      background-color: var(--flight-background-color);
      box-shadow: 0 0 0 var(--spacing-2) var(--flight-color);
    }
    &.ferry {
      background-color: var(--ferry-background-color);
      box-shadow: 0 0 0 var(--spacing-2) var(--ferry-color);
    }
  }
  .trip-type-card-header {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-16);
    border-bottom: 1px solid var(--color-secondary-85);
    padding-bottom: var(--spacing-12);
    margin-bottom: var(--spacing-12);
    .trip-type-card-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-12);
      border-radius: var(--spacing-32);
      border: 3px solid var(--color-secondary-40);
      height: fit-content;
      margin: auto 0;
      @include for-small-devices-max {
        padding: var(--spacing-8);
      }
    }
    .trip-type-card-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: var(--spacing-4);
      .trip-type-title {
        @include font-label-xl-bold;
      }
      .trip-type-description {
        color: var(--color-secondary-60);
        @include font-label-l-light;
      }
    }
  }
  .trip-type-card-details {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    .trip-type-card-location {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);
      width: 100%;
      .trip-type-card-location-label {
        @include font-label-l-light;
        color: var(--color-secondary-60);
        @include for-small-devices-max {
          @include font-label-s-light;
        }
      }
      .trip-type-card-location-value {
        display: flex;
        justify-content: space-between;
        .trip-type-card-location-destination-value {
          @include font-label-xl-bold;
          @include for-small-devices-max {
            @include font-label-s-bold;
          }
        }
        .trip-type-card-edit {
          @include font-label-l-light;
          @include for-small-devices-max {
            @include font-label-s-light;
          }
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
